'use client';

import { useLayoutEffect } from 'react';
import { usePathname } from 'next/navigation';

type HashScrollHandlerProps = {
  offset?: number;
  maxAttempts?: number;
  delayMs?: number;
  startDelayMs?: number;
};

function escapeSelectorValue(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value);
  }

  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function findTargetElement(targetId: string): HTMLElement | null {
  return (
    document.getElementById(targetId) ??
    document.querySelector<HTMLElement>(
      `[data-testid="${escapeSelectorValue(targetId)}"]`,
    )
  );
}

function getHashTargetId(): string | null {
  if (typeof window === 'undefined') return null;

  const hash = window.location.hash;

  if (!hash) return null;

  return decodeURIComponent(hash.replace('#', ''));
}

export default function HashScrollHandler({
  offset = 112,
  maxAttempts = 40,
  delayMs = 100,
  startDelayMs = 300,
}: HashScrollHandlerProps) {
  const pathname = usePathname();

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;

    const targetId = getHashTargetId();

    if (!targetId) return;

    const scrollOffset = offset;
    const shouldFocus = true;

    let attempts = 0;
    let timeoutId: number | undefined;
    let settleTimeoutId: number | undefined;
    let isDone = false;
    let allowManualCancel = false;

    const scrollToTarget = () => {
      if (isDone) return;

      const target = findTargetElement(targetId);

      attempts += 1;

      if (!target) {
        if (attempts < maxAttempts) {
          timeoutId = window.setTimeout(scrollToTarget, delayMs);
        }

        return;
      }

      const top =
        target.getBoundingClientRect().top + window.scrollY - scrollOffset;

      window.scrollTo({
        top,
        behavior: attempts === 1 ? 'smooth' : 'auto',
      });

      allowManualCancel = true;

      if (attempts < maxAttempts) {
        timeoutId = window.setTimeout(scrollToTarget, delayMs);
        return;
      }

      if (shouldFocus) {
        const hadTabIndex = target.hasAttribute('tabindex');

        if (!hadTabIndex) {
          target.setAttribute('tabindex', '-1');
        }

        window.setTimeout(() => {
          if (!isDone) {
            target.focus({ preventScroll: true });
          }
        }, 350);
      }

      cleanup();
    };

    function scheduleScrollToTarget() {
      if (isDone) return;

      if (settleTimeoutId) {
        window.clearTimeout(settleTimeoutId);
      }

      settleTimeoutId = window.setTimeout(() => {
        scrollToTarget();
      }, 80);
    }

    function cancelAutoScroll() {
      if (!allowManualCancel || isDone) return;

      cleanup();
    }

    const resizeObserver = new ResizeObserver(() => {
      scheduleScrollToTarget();
    });

    const mutationObserver = new MutationObserver(() => {
      scheduleScrollToTarget();
    });

    function cleanup() {
      isDone = true;

      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }

      if (settleTimeoutId) {
        window.clearTimeout(settleTimeoutId);
      }

      resizeObserver.disconnect();
      mutationObserver.disconnect();

      window.removeEventListener('load', scheduleScrollToTarget);
      window.removeEventListener('resize', scheduleScrollToTarget);
      window.removeEventListener('wheel', cancelAutoScroll);
      window.removeEventListener('touchmove', cancelAutoScroll);
      window.removeEventListener('keydown', cancelAutoScroll);
      window.removeEventListener('mousedown', cancelAutoScroll);
    }

    timeoutId = window.setTimeout(scrollToTarget, startDelayMs);

    resizeObserver.observe(document.body);

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    window.addEventListener('load', scheduleScrollToTarget);
    window.addEventListener('resize', scheduleScrollToTarget);
    window.addEventListener('wheel', cancelAutoScroll, { passive: true });
    window.addEventListener('touchmove', cancelAutoScroll, { passive: true });
    window.addEventListener('keydown', cancelAutoScroll);
    window.addEventListener('mousedown', cancelAutoScroll);

    return cleanup;
  }, [pathname, offset, maxAttempts, delayMs, startDelayMs]);

  return null;
}
